import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Download, Trash2, MessageCircle, Mail, Plus, Calendar, User, Sparkles, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import SEO from "@/components/SEO";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { waLink } from "@/lib/whatsapp";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Negotiation", "Won", "Lost", "Follow-up"];
const STATUS_COLORS = {
  New: "bg-cyan-500/20 text-cyan-300", Contacted: "bg-blue-500/20 text-blue-300",
  Qualified: "bg-violet-500/20 text-violet-300", "Proposal Sent": "bg-indigo-500/20 text-indigo-300",
  Negotiation: "bg-amber-500/20 text-amber-300", Won: "bg-emerald-500/20 text-emerald-300",
  Lost: "bg-red-500/20 text-red-300", "Follow-up": "bg-orange-500/20 text-orange-300",
};

const QUALITY_COLORS = { Hot: "bg-red-500/20 text-red-300", Warm: "bg-amber-500/20 text-amber-300", Cold: "bg-sky-500/20 text-sky-300" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);

  const load = () => {
    const q = new URLSearchParams({ search, status, sort, order }).toString();
    api.get(`/admin/leads?${q}`).then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, status, sort, order]);

  const openLead = (id) => api.get(`/admin/leads/${id}`).then((r) => { setActive(r.data); setReply(""); }).catch(() => {});

  const suggestReply = async () => {
    setReplyLoading(true);
    try {
      const { data } = await api.post(`/admin/leads/${active.id}/suggest-reply`);
      setReply(data.reply || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI generation failed");
    } finally { setReplyLoading(false); }
  };

  const update = async (id, patch) => {
    const { data } = await api.patch(`/admin/leads/${id}`, patch);
    setActive(data); load(); toast.success("Updated");
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/leads/${active.id}/notes`, { text: note });
    setNote(""); openLead(active.id); toast.success("Note added");
  };
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); setActive(null); load(); toast.success("Lead deleted"); };

  const exportUrl = `${API}/admin/leads-export?token=${localStorage.getItem("nsa_token")}`;

  return (
    <div>
      <SEO title="Leads CRM | Admin" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Leads / CRM</h1><p className="text-slate-400 text-sm">{leads.length} leads</p></div>
        <a href={exportUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="border-white/20 text-slate-200 hover:bg-white/10" data-testid="export-leads"><Download className="w-4 h-4 mr-1" /> Export CSV</Button></a>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, service..." className="pl-9 bg-white/5 border-white/10 text-white" data-testid="lead-search" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="lead-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={`${sort}:${order}`} onValueChange={(v) => { const [s, o] = v.split(":"); setSort(s); setOrder(o); }}>
          <SelectTrigger className="w-44 bg-white/5 border-white/10 text-white" data-testid="lead-sort"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="created_at:desc">Newest first</SelectItem><SelectItem value="created_at:asc">Oldest first</SelectItem><SelectItem value="full_name:asc">Name A–Z</SelectItem><SelectItem value="quality_score:desc">Best quality first</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="mt-5 rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white/5 text-slate-400"><tr>
            <th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Service</th><th className="text-left p-3">Budget</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th>
          </tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => openLead(l.id)} data-testid={`lead-row-${l.id}`} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                <td className="p-3 font-medium">{l.full_name}</td>
                <td className="p-3 text-slate-400 text-xs">{l.email}<br />{l.whatsapp}</td>
                <td className="p-3 text-slate-400">{l.service || "—"}</td>
                <td className="p-3 text-slate-400">{l.budget || "—"}</td>
                <td className="p-3">{l.quality ? <Badge className={QUALITY_COLORS[l.quality] || "bg-white/10 text-slate-300"}>{l.quality}</Badge> : <span className="text-slate-600 text-xs">—</span>}</td>
                <td className="p-3"><Badge className={STATUS_COLORS[l.status]}>{l.status}</Badge></td>
                <td className="p-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-slate-900 text-slate-100 border-white/10">
          {active && (
            <>
              <SheetHeader><SheetTitle className="text-white">{active.full_name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="flex gap-2">
                  <a href={waLink(active.whatsapp, `Hi ${active.full_name}, thanks for reaching out to NewStarAds.`)} target="_blank" rel="noreferrer" className="flex-1"><Button className="w-full bg-green-600 hover:bg-green-700"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button></a>
                  <a href={`mailto:${active.email}`} className="flex-1"><Button variant="outline" className="w-full border-white/20 text-slate-200"><Mail className="w-4 h-4 mr-1" /> Email</Button></a>
                </div>

                <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-primary flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI reply assistant</span>
                    <Button size="sm" variant="secondary" onClick={suggestReply} disabled={replyLoading} data-testid="lead-suggest-reply">
                      {replyLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Drafting…</> : "Suggest reply"}
                    </Button>
                  </div>
                  {reply && (
                    <>
                      <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={6} className="bg-white/5 border-white/10 text-white text-sm" data-testid="lead-reply-text" />
                      <div className="flex gap-2">
                        <a href={waLink(active.whatsapp, reply)} target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" className="w-full bg-green-600 hover:bg-green-700"><MessageCircle className="w-3.5 h-3.5 mr-1" /> Send on WhatsApp</Button></a>
                        <Button size="sm" variant="outline" className="border-white/20 text-slate-200" onClick={() => { navigator.clipboard.writeText(reply); toast.success("Copied"); }} data-testid="lead-reply-copy"><Copy className="w-3.5 h-3.5" /></Button>
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-500">Status</Label>
                    <Select value={active.status} onValueChange={(v) => update(active.id, { status: v })}><SelectTrigger className="bg-white/5 border-white/10 text-white mt-1" data-testid="lead-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label className="text-xs text-slate-500">Follow-up date</Label><Input type="date" value={active.follow_up_date?.slice(0,10) || ""} onChange={(e) => update(active.id, { follow_up_date: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" /></div>
                </div>
                <div><Label className="text-xs text-slate-500">Assigned to</Label><Input value={active.assigned_to || ""} onBlur={(e) => update(active.id, { assigned_to: e.target.value })} onChange={(e) => setActive({ ...active, assigned_to: e.target.value })} placeholder="Team member" className="bg-white/5 border-white/10 text-white mt-1" /></div>

                <div className="rounded-lg border border-white/10 p-4 text-sm space-y-1.5">
                  {[["Email", active.email], ["WhatsApp", active.whatsapp], ["Country", active.country], ["Goal", active.goal], ["Audience", active.target_audience], ["Channel", active.youtube_channel_url], ["Video", active.youtube_video_url], ["Source", active.utm_source || active.source], ["Campaign", active.utm_campaign], ["Landing", active.landing_page], ["Device", active.device]].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4"><span className="text-slate-500">{k}</span><span className="text-slate-300 text-right break-all">{v}</span></div>
                  ))}
                </div>
                {active.message && <div className="rounded-lg bg-white/5 p-4 text-sm text-slate-300">{active.message}</div>}

                <div>
                  <Label className="text-xs text-slate-500">Notes & history</Label>
                  <div className="mt-2 space-y-2 max-h-52 overflow-y-auto">
                    {(active.notes || []).map((n) => (
                      <div key={n.id} className="rounded-lg bg-white/5 p-3 text-sm"><p className="text-slate-300">{n.text}</p><p className="text-xs text-slate-500 mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>
                    ))}
                    {(!active.notes || active.notes.length === 0) && <p className="text-xs text-slate-500">No notes yet.</p>}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note..." className="bg-white/5 border-white/10 text-white" data-testid="lead-note-input" />
                    <Button onClick={addNote} data-testid="lead-note-add"><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>

                <Button variant="ghost" className="text-red-400 hover:text-red-300 w-full" onClick={() => del(active.id)} data-testid="lead-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete lead</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
